import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { CharterStatus } from '@ankor-io/common/types/charter'
import {
  TransactionTrackingCategoryPayload,
  XeroConnectionSyncStatus,
} from '@ankor-io/common/types/integrations/xero/types'
import { CategoryMetadata, CategoryPayload, CategoryType } from '@ankor-io/common/types/transaction/category'
import { TaxLabelPayload } from '@ankor-io/common/types/transaction/tax'

/**
 * Backend transaction type
 */

export type Transaction = {
  id: string
  type: TransactionType
  amountCents: number
  currencyCode: string
  status: TransactionStatus
  vatCode?: VatCode
  vat?: string
  createdAt: string
  charterId?: string
  categoryId?: string
  vendor?: string
  comments?: string
  originalAmountCents: number
  originalAmountCurrencyCode: string
  conversionRateStr?: string
  payableVatAmountCents: number
  date: string
  createdBy?: string
  awxConnectedAccountId: string
  paymentMethod: PaymentMethod
  awxActivityEventId?: string
  settlementStatus?: TransactionSettlementStatus
  cashFloatId?: string
  // more fields to be added likely when we start dealing with AWX transactions
  settledById?: string
  settledAt?: string
  xeroTrackingOption1Id?: string
  xeroTrackingOption2Id?: string
  taxLabelId?: string
}

export type TransactionCashFloatPayload = {
  id: string
  name: string
  currencyCode: string
}

/**
 * The types of transactions supported by the system. A transaction type infers different behaviors on the transaction object.
 */
export enum TransactionType {
  /**
   * An Ankor Pay transaction type is generated when spenders pay with AnkorPay+Airwallex cards
   */
  ANKOR_PAY = 'ANKOR_PAY',
  /**
   * A transaction type manually created by the user
   * TODO: Perhaps we should eventually consider renaming this to MANUAL_EXPENSE
   */
  MANUAL = 'MANUAL',
  /**
   * A transaction type for gratuity, cannot be manually created by the user nor edited with the traditional update transaction endpoint.
   * It can only be edited during sign off via the gratuity calculator in the charter ledger.
   */
  GRATUITY = 'GRATUITY',
  /**
   * Represents a cash float transfer transaction.
   * If this type is present, then we know this transaction
   * is paired with another CASH_FLOAT_TRANSFER transaction
   */
  CASH_FLOAT_TRANSFER = 'CASH_FLOAT_TRANSFER',
  /**
   * Manual credit added to a cash float.
   * Always positive amountCents and originalAmountCents.
   * Auto-approved on creation.
   */
  CASH_FLOAT_TOP_UP = 'CASH_FLOAT_TOP_UP',
}

export const EXPENSE_TRANSACTION_TYPES = [
  TransactionType.MANUAL,
  TransactionType.ANKOR_PAY,
  TransactionType.GRATUITY,
] as const

export type ExpenseTransactionType = (typeof EXPENSE_TRANSACTION_TYPES)[number]

/**
 * The transaction statuses supported by the system
 */
export enum TransactionStatus {
  /**
   * A transaction is incomplete when VAT, Charter, or Category attributions are missing
   */
  INCOMPLETE = 'INCOMPLETE',
  /**
   * A transaction moves to submitted when the VAT and Charter attributions are defined
   */
  SUBMITTED = 'SUBMITTED',
  /**
   * Status required by multi-stage workflow approvals (e.g. 1 approval over 2 required)
   */
  PARTIALLY_APPROVED = 'PARTIALLY_APPROVED',
  /**
   * The transaction has been approved by a user
   */
  APPROVED = 'APPROVED',
  /**
   * The transaction has been rejected by a user
   */
  REJECTED = 'REJECTED',
}

export enum TransactionSettlementStatus {
  PENDING = 'PENDING',
  SETTLED = 'SETTLED',
  MARKED_AS_SETTLED = 'MARKED_AS_SETTLED',
}

export enum VatCode {
  /**
   * When a transaction is VAT inclusive, the VAT amount is included in the transaction amount
   */
  VAT_INCLUSIVE = 'VAT_INCLUSIVE',
  /**
   * When a transaction is VAT exclusive, the VAT amount is not included in the transaction amount.
   * The user can decide to add the vat amount to the `vat` field. If the user does not set the vat field,
   * then the vat amount fall back to the charter vat amount which must be set.
   */
  VAT_EXCLUSIVE = 'VAT_EXCLUSIVE',
  /**
   * When a transaction is VAT NA, I guess the user doesn't know and therefore we do nothing.
   */
  VAT_NA = 'VAT_NA',
}

/**
 * The payment methods supported by the system.
 */
export enum PaymentMethod {
  /**
   * Used for transaction rows that are ledger movements but not payments,
   * such as cash float top-ups.
   */
  NONE = 'NONE',
  /**
   * A transaction has an AnkorPay payment method when it has an awx_transaction_id associated with it.
   * Any transaction from a card spent on AnkorPay is an AnkorPay transaction.
   *
   * This payment method cannot be selected by the user when logging a manual expense.
   */
  ANKOR_PAY = 'ANKOR_PAY',
  BOAT_CASH = 'BOAT_CASH',
  BOAT_STOCK = 'BOAT_STOCK',
  BOAT_APA_CARD = 'BOAT_APA_CARD',
  PERSONAL_CASH_OR_CARD = 'PERSONAL_CASH_OR_CARD',
  TRANSFER = 'TRANSFER',
  NON_ANKORPAY_BANK_TRANSFER = 'NON_ANKORPAY_BANK_TRANSFER',
  OTHER = 'OTHER',
}

/**
 * The payload of a transaction. To be used in listing transactions
 */
export type TransactionPayload = {
  id: string
  type: TransactionType
  amountCents: number
  currencyCode: string
  status: TransactionStatus
  awxTransactionId?: string
  vatCode?: VatCode
  vat?: string
  charter?: {
    id: string
    name: string
    status: CharterStatus
  }
  category?: {
    id: string
    name: string
    color: string
    awxConnectedAccountType: AwxConnectedAccountType
    icon?: string
    type: CategoryType
    metadata?: CategoryMetadata
  }
  taxLabel?: Pick<TaxLabelPayload, 'id' | 'xeroTaxType' | 'name' | 'color' | 'icon' | 'type'>

  vendor?: string
  comments?: string
  originalAmountCents: number
  originalAmountCurrencyCode: string
  conversionRateStr?: string
  payableVatAmountCents: number
  date: string
  createdAt: string
  createdBy?: string
  transactedBy: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  awxConnectedAccountId: string
  paymentMethod: PaymentMethod
  awxActivityEvent?: {
    id: string
    type: AWXActivityEventType
    // this will be of either 3 type of status, for now we only have one so we will use or string
    status: AWXCardActivityEventStatus | AWXTransferActivityEventStatus | string
    // same as the field above this data can be of 3 different types but for now we only have one
    data: AWXCardActivityEventSummaryPayload | AWXTransferActivityEventPayload | unknown
  }
  settlementStatus?: TransactionSettlementStatus
  settledById?: string
  settledBy?: {
    firstName: string
    lastName: string
    email: string
  }
  settledAt?: string
  /** Latest Xero sync state for this transaction, when it has been included in a Xero sync attempt. */
  xeroSync?: {
    status: XeroConnectionSyncStatus
    finishedAt?: string
    updatedAt: string
  }
  cashFloat?: TransactionCashFloatPayload
  /** Assigned tracking category options.
   * Only present for ANKOR_PAY transactions with Xero integration active.
   * This is a list of tracking options that have been assigned to the transaction.
   * This is used to display the tracking options in the transaction details page. */
  xeroTrackingCategories?: TransactionTrackingCategoryPayload[]
  transferDestinationTransaction?: LinkedTransferTransaction
  transferSourceTransaction?: LinkedTransferTransaction
}

export type AWXActivityEventPayload = {
  id: string
  type: AWXActivityEventType
  // this will be of either 3 type of status, for now we only have one so we will use or string
  status:
    | AWXCardActivityEventStatus
    | AWXTransferActivityEventStatus
    | AWXConnectedAccountTransferActivityEventStatus
    | string
  // same as the field above this data can be of 3 different types but for now we only have one
  data:
    | AWXCardActivityEventPayload
    | AWXTransferActivityEventPayload
    | AWXConnectedAccountTransferActivityEventWrapperPayload
    | unknown
}

// this is the transaction payload for a get single transaction endpoint
export type FullTransactionPayload = Omit<TransactionPayload, 'charter'> & {
  charter?: {
    id: string
    name: string
    status: CharterStatus
  }
  awxActivityEvent?: AWXActivityEventPayload
  // only adding this here so I don't have to modify the listing query but only the get transaction query
  // additionally this field is not required in listing
  charterCurrencyCode?: string
}

export type CreateTransactionBody = {
  categoryId?: string
  vendor?: string
  comments?: string
  originalAmountCents: number
  originalAmountCurrencyCode: string
  date?: string
  paymentMethod: PaymentMethod
  cashFloatId?: string
}

export type CreateOpsManualTransactionBody = CreateTransactionBody & {
  // Temporary: OPS form still on vat/vatCode until the tax-label UI ships. Re-tighten taxLabelId and drop vatCode/vat then.
  taxLabelId?: string
  vatCode?: VatCode
  vat?: string
}

export type CreateApaManualTransactionBody = CreateTransactionBody & {
  vatCode: VatCode
  vat?: string
}

export type CreateTransactionRequest = {
  /**
   * Optional override for the row's id. Set when the caller needs the id known
   * before insert (e.g. for cross-row FK references in a multi-row write).
   * Otherwise Prisma's @default(uuid()) generates one.
   */
  id?: string
  type: TransactionType
  amountCents: number
  currencyCode: string
  status: TransactionStatus
  awxTransactionId?: string
  vatCode?: string
  vat?: string
  charterId?: string
  categoryId?: string
  taxLabelId?: string
  vendor?: string
  comments?: string
  originalAmountCents: number
  originalAmountCurrencyCode: string
  conversionRateStr?: string
  /** Only set for APA manual transactions. */
  payableVatAmountCents?: number
  date: string
  createdBy?: string
  awxConnectedAccountId: string
  paymentMethod: PaymentMethod
  settlementStatus?: TransactionSettlementStatus
  /** Cash float this expense is charged against. Required when `paymentMethod` is `BOAT_CASH`. */
  cashFloatId?: string
  xeroTrackingOption1Id?: string
  xeroTrackingOption2Id?: string
  transferDestinationId?: string
}

export type UpdateManualTransactionRequest = {
  amountCents?: number
  currencyCode?: string
  status?: TransactionStatus
  vatCode?: string
  vat?: string
  // The user can explicitly decide to not tag this transaction to a charter, which is why we allow null
  charterId?: string | null
  // The user can explicitly decide to not tag this transaction to a category, which is why we allow null
  categoryId?: string | null
  // The user can explicitly decide to not tag this transaction to a tax label, which is why we allow null
  taxLabelId?: string | null
  vendor?: string
  comments?: string
  originalAmountCents?: number
  originalAmountCurrencyCode?: string
  conversionRateStr?: string | null
  payableVatAmountCents?: number
  date?: string
  paymentMethod?: PaymentMethod
  awxActivityEventId?: string
  settlementStatus?: TransactionSettlementStatus // Used for toggling
  /** Cash float this expense is charged against. Required when `paymentMethod` is `BOAT_CASH`. */
  cashFloatId?: string
}

export type UpdateAnkorPayTransactionRequest = {
  awxTransactionId?: string
  status?: TransactionStatus
  vatCode?: string
  vat?: string
  // The user can explicitly decide to not tag this transaction to a charter, which is why we allow null
  charterId?: string | null
  // The user can explicitly decide to not tag this transaction to a category, which is why we allow null
  categoryId?: string | null
  vendor?: string
  comments?: string
  payableVatAmountCents?: number
  // The user can explicitly decide to not tag this transaction to a tax label, which is why we allow null
  taxLabelId?: string | null
  // exposes but only editable by the backend
  amountCents?: number
  // exposed but only editable by the backend
  amountCurrency?: string
  // exposed but only editable by the backend
  conversionRateStr?: string
  /**
   * These two fields are only needed if xero tracking is active.
   * An admin can configure whether a tracking category is REQUIRED or not.
   * If the tracking category is not REQUIRED, then we expect a null here.
   */
  xeroTrackingOption1Id?: string | null
  xeroTrackingOption2Id?: string | null
}

export type PrepareTransactionAttachmentUploadRequest = {
  name: string
  mimeType: string
  transactionId: string
  uploadedBy: string
}

export type PrepareTransactionAttachmentUploadResponse = {
  id: string
  uploadUrl: string
  uploadToken: string
}

export type ConfirmTransactionAttachmentUploadRequest = {
  uploadToken: string
}

export type CreateTransactionAttachmentRequest = {
  name: string
  mimeType: string
  transactionId: string
  uploadedBy: string
  storageKey?: string
}

export enum TransactionAttachmentStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
}

export type TransactionAttachment = {
  id: string
  transactionId: string
  name: string
  mimeType: string
  uploadedAt: string
  storageKey: string
  uploadedBy: string
}

// Shared with the list-transactions Zod schema so the type and allowlist stay in sync.
export const TRANSACTION_SORT_BY = [
  'createdAt',
  'amountCents',
  'status',
  'vendor',
  'date',
  'charterName',
  'categoryName',
] as const

export type TransactionSortBy = (typeof TRANSACTION_SORT_BY)[number]

export type ListTransactionsRequest = {
  status?: TransactionStatus[]
  type?: TransactionType[]
  charterId?: string
  cashFloatId?: string
  categoryId?: string
  vendor?: string
  dateFrom?: string
  dateTo?: string
  amountMinCents?: number
  amountMaxCents?: number
  take?: number
  skip?: number
  sortBy?: TransactionSortBy
  sortOrder?: 'asc' | 'desc'
  charterTagged?: boolean
  createdBy?: string
  createdByIds?: string[]
  paymentMethod?: PaymentMethod[]
  /**
   * If true, filters out any transaction that was created from a failed card activity event
   */
  excludeFailed?: boolean
  /**
   * If true, only transactions with a category_id are returned. Used by Xero sync so uncategorized expenses are not synced.
   * NOTE: only supported for `listTransactionIds` (pay-gateway).
   */
  requireCategory?: boolean
  // NOTE: this filter is only supported for the list transaction ids query
  activityEventFilter?: ActivityEventFilter
  settlementStatus?: TransactionSettlementStatus[] // Needed for filtering
}

export enum ActivityEventFilter {
  // includes transactions without activity events
  NONE = 'NONE',
  // includes transactions without and with activity events
  ANY = 'ANY',
  // includes transactions with activity events that are card activity events
  CARD_ONLY = 'CARD_ONLY',
  // includes transactions with activity events that are card activity events OR transfer events
  CARD_OR_TRANSFER = 'CARD_OR_TRANSFER',
}

export type ListApaOwesOpsTransactionsAndSummaryRequest = {
  take?: number
  skip?: number
  charterId?: string
  orderBy?: 'asc' | 'desc'
}

/**
 * Lean transaction type for the "APA owes OPS" list.
 * We don't need the full `TransactionPayload` here — only the fields
 * required by the settlement selection UI.
 */
export type ApaOwesOpsTransaction = {
  id: string
  date: string
  charter: {
    id: string
    name: string
  }
  category: CategoryPayload | null
  paymentMethod: PaymentMethod
  amountCents: number
  currency: string
  settlementStatus: TransactionSettlementStatus | null
  transferAttempts: ConnectedAccountTransferAttemptPayload[]
  /** Present when `paymentMethod` is boat cash and the row is linked to a float. */
  cashFloat?: TransactionCashFloatPayload
}

export type ConnectedAccountTransferAttemptPayload = Pick<
  AWXConnectedAccountTransferActivityEventPayload,
  'id' | 'status' | 'createdAt'
>

export type ApaOwesOpsTransactionsAndSummaryPayload = {
  transactions: ApaOwesOpsTransaction[]
  // More fields can be added within the summary in the future
  summary: {
    // eg: `"EUR": 45000, "USD": 12000`
    totalAmountByCurrency: Record<string, number>
  }
}

export type TransactionAttachmentPayload = Omit<
  TransactionAttachment,
  'uploadedBy' | 'storageKey' | 'transactionId'
> & {
  url: string
}

export type CreateTransactionAttachmentBody = {
  name: string
  mimeType: string
}

export type PreSignedAttachmentUploadBody = {
  filename: string
}

export type UpdateManualTransactionBody = Omit<
  UpdateManualTransactionRequest,
  'amountCents' | 'currencyCode' | 'status' | 'payableVatAmountCents'
> & {
  charterSelected: boolean
}
export type UpdateAnkorPayTransactionBody = Omit<UpdateAnkorPayTransactionRequest, 'status'> & {
  charterSelected: boolean
}

export type UpdateCashLedgerTransactionBody = {
  comments?: string
  originalAmountCents?: number
  date?: string
}

// Temporary: OPS form still sends vat/vatCode until the tax-label UI ships. Re-add them to the Omit list then.
export type UpdateOpsManualTransactionBody = Omit<
  UpdateManualTransactionRequest,
  'charterId' | 'amountCents' | 'currencyCode' | 'status' | 'payableVatAmountCents' | 'conversionRateStr'
>
export type UpdateOpsAnkorPayTransactionBody = Omit<
  UpdateAnkorPayTransactionRequest,
  'charterId' | 'status' | 'payableVatAmountCents'
>

export type AWXActivityEvent = {
  id: string
  awxConnectedAccountId: string
  type: AWXActivityEventType
  eventId: string
  createdAt: string
}

export type AWXCardActivityEvent = {
  id: string
  cardId: string
  transactionAmountCents: number
  transactionCurrencyCode: string
  billingAmountCents: number
  billingCurrencyCode: string
  transactionDate: string
  transactionType: string
  status: AWXCardActivityEventStatus
  merchantId: string
  merchantName: string
  merchantCountry: string
  merchantCode: string
  merchantCity: string
  // can be of type PIN_INVALID, CVV_INVALID, PIN_RETRIES_EXCEEDED, CARDHOLDER_VERIFICATION_FAILED, INVALID_EXPIRY_DATE, TRANSACTION_BLOCKED
  // from https://www.airwallex.com/docs/api/Issuing/Cards/_api_v1_issuing_cards_create/post#/Simulation_(Sandbox_Only)/Issuing/_api_v1_simulation_issuing_create/post
  failureReason: string
  fees: AWXActivityEventFee[]
}

// this type is to be used for list payload
export type AWXCardActivityEventSummaryPayload = {
  card: {
    id: string
    nickName: string
    maskedCardNumber: string
  }
}

export type AWXConnectedAccountTransferActivityEventWrapperPayload = {
  type: string
  createdAt: string
  transferAttempts: AWXConnectedAccountTransferActivityEventPayload[]
}

export type AWXConnectedAccountTransferActivityEventPayload = {
  id: string
  amountCents: number
  currencyCode: string
  targetAWXConnectedAccountId: string
  requestId: string
  referenceId?: string
  status: AWXConnectedAccountTransferActivityEventStatus
  feeCents?: number
  reason?: string
  createdAt: string
  updatedAt?: string
}
/**
 * see {@link AWXTransferActivityEvent} for info on fields
 */
export type AWXTransferActivityEventPayload = {
  amountPayersPayCents: number
  createdAt: string
  dispatchDate: string
  failureReason?: string
  failureType?: string
  feePayer: FeePayer
  transferId: string
  metadata?: { [key: string]: any }
  payerId: string
  reason?: string
  reference?: string
  receiveAmountCents: number
  receiveCurrencyCode: string
  status: AWXTransferActivityEventStatus
  transferAmountCents: number
  transferCurrencyCode: string
  updatedAt: string
}

// this type is to be used for a single transaction payload
export type AWXCardActivityEventPayload = AWXCardActivityEventSummaryPayload & {
  // available when the status is failed
  failureReason?: string
  // the amount charged in the local currency on the card
  amountCents: number
  // the local currency
  amountCurrencyCode: string
  // the amount charged to the airwallex account, either in the local currency if available or the default one
  billingAmountCents: number
  // the currency charged in airwallex
  billingCurrencyCode: string
  fees: Omit<AWXActivityEventFee, 'id'>[]
  merchant?: {
    id?: string
    name?: string
    country?: string
    code?: string
    city?: string
  }
}

export type AWXActivityEventFee = {
  id: string
  amountCents: number
  currencyCode: string
  type: string
  details?: string
  payFeeId?: string
}

export enum AWXCardActivityEventStatus {
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  PENDING = 'pending',
  REFUNDED = 'refunded',
}

export enum AWXActivityEventType {
  CARD_TRANSACTION = 'CARD_TRANSACTION',
  CONVERSION = 'CONVERSION',
  DEPOSIT = 'DEPOSIT',
  TRANSFER = 'TRANSFER',
  CONNECTED_ACCOUNT_TRANSFER = 'CONNECTED_ACCOUNT_TRANSFER',
}

export type CardTransaction = {
  cardId: string
  transactionId: string
}

export type CardTransactionStats = {
  id: string
  type: AWXActivityEventType
  // this will be of either 3 type of status, for now we only have one so we will use or string
  status: AWXCardActivityEventStatus
  amountCents: number
  amountCurrencyCode: string
  transactionDate: string
}

export type CardTransactionPayload = {
  /**
   * This is the id of our system transaction which the client can use to redirect to the transaction view page
   */
  transactionId: string
  /**
   * The AWX card activity event associated with this transaction
   */
  awxCardActivityEvent: Omit<AWXCardActivityEvent, 'fees'>
}

export type DepositPayload = {
  /**
   * The id of the deposit
   */
  id: string
  /**
   * The amount of the deposit in cents
   */
  amountCents: number
  /**
   * The currency of the deposit
   */
  currencyCode: string
  /**
   * The status of the deposit
   */
  status: AWXDepositActivityStatus
  /**
   * The type of the deposit
   */
  type: AWXDepositActivityType
  /**
   * The failure code of the deposit
   */
  failureCode?: string
  /**
   * The failure ISO code of the deposit
   */
  failureIsoCode?: string
  /**
   * The reference of the deposit
   */
  reference?: string
  /**
   * The date the deposit was made
   */
  depositDate: string
  /**
   * The date the deposit was settled, undefined if the deposit is not settled or failed
   */
  settledAt?: string
  /**
   * The charter associated with the deposit, undefined if the deposit is not tagged to a charter
   */
  charter?: {
    /**
     * The id of the charter
     */
    id: string
    /**
     * The name of the charter
     */
    name: string
    /**
     * The status of the charter
     */
    status: CharterStatus
  }
}

export type AWXConnectedAccountTransferActivityEventWrapper = {
  id: string
  type: string
  createdAt: string
}

export type AWXConnectedAccountTransferActivityEvent = {
  id: string
  amountCents: number
  currencyCode: string
  targetAWXConnectedAccountId: string
  requestId: string
  referenceId?: string
  status: AWXConnectedAccountTransferActivityEventStatus
  feeCents?: number
  reason?: string
  createdAt: string
  updatedAt?: string
  initiatedBy?: string
}

export enum AWXConnectedAccountTransferActivityEventStatus {
  NEW = 'NEW',
  SETTLED = 'SETTLED',
  PENDING = 'PENDING',
  SUSPENDED = 'SUSPENDED',
  FAILED = 'FAILED',
}

export type AWXDepositActivityEvent = {
  id: string
  amountCents: number
  currencyCode: string
  status: AWXDepositActivityStatus
  type: AWXDepositActivityType
  failureCode?: string
  failureIsoCode?: string
  reference?: string
  depositDate: string
  settledAt?: string
  charterId?: string | null
}

export enum AWXDepositActivityStatus {
  PENDING = 'PENDING',
  SETTLED = 'SETTLED',
  REJECTED = 'REJECTED',
  REVERSED = 'REVERSED',
}

export enum AWXDepositActivityType {
  BANK_TRANSFER = 'BANK_TRANSFER',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  DIGITAL_WALLET_TRANSFER = 'DIGITAL_WALLET_TRANSFER',
  AGGREGATE_FUNDS = 'AGGREGATE_FUNDS',
}

export type AWXConversionActivityEvent = {
  id: string
  awxRate: string
  buyAmountCents: number
  buyAmountCurrencyCode: string
  sellAmountCents: number
  sellAmountCurrencyCode: string
  conversionDate: string
  conversionId: string
  status: AWXConversionActivityEventStatus
  createdAt: string
  updatedAt: string
  // This is a stringified array of rate details, mostly stored for info tracking purposes
  // no business logic on this field
  rateDetails: string
}

export enum AWXConversionActivityEventStatus {
  SCHEDULED = 'SCHEDULED',
  CANCELLED = 'CANCELLED',
  OVERDUE = 'OVERDUE',
  SETTLED = 'SETTLED',
}

export type AWXTransferActivityEvent = {
  // the id of the event
  id: string
  // the amount the payer will pay
  amountPayersPayCents: number
  // the id of the beneficiary
  beneficiaryId: string
  // the date the transfer was created
  createdAt: string
  // the date the transfer was dispatched
  /**
   * @deprecated this field never comes back
   */
  dispatchDate: string
  // the external reference of the transfer
  /**
   * @deprecated this field never comes back
   */
  externalReference?: string
  // the failure reason of the transfer, only available when the status failed
  failureReason?: string
  // the failure type of the transfer, only available when the status failed
  failureType?: string
  // the role that pays the fee
  feePayer: FeePayer
  // the id of the transfer
  transferId: string
  // arbitrary metadata we can store fields here and we get them back via webhook
  metadata?: { [key: string]: any }
  // the id of the payer
  /**
   * @deprecated this field never comes back
   */
  payerId: string
  // the reason of the transfer, only available when the status failed
  reason?: string
  // the reference of the transfer
  reference?: string
  // the amount the beneficiary will receive
  receiveAmountCents: number
  // the currency the beneficiary will receive
  receiveCurrencyCode: string
  // the amount the payer will pay
  transferAmountCents: number
  // the currency the payer will pay
  transferCurrencyCode: string
  // the status of the transfer
  status: AWXTransferActivityEventStatus
  // the date the transfer was updated
  updatedAt: string
}

export enum FeePayer {
  PAYER = 'PAYER',
  BENEFICIARY = 'BENEFICIARY',
}

// see https://www.airwallex.com/docs/payouts__create-a-transfer__transfer-statuses
export enum AWXTransferActivityEventStatus {
  IN_APPROVAL = 'IN_APPROVAL',
  APPROVAL_RECALLED = 'APPROVAL_RECALLED',
  APPROVAL_REJECTED = 'APPROVAL_REJECTED',
  APPROVAL_BLOCKED = 'APPROVAL_BLOCKED',
  SCHEDULED = 'SCHEDULED',
  OVERDUE = 'OVERDUE',
  PROCESSING = 'PROCESSING',
  SENT = 'SENT',
  PAID = 'PAID',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type LinkedTransferTransaction = {
  id: string
  cashFloat?: TransactionCashFloatPayload
}
